import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Slide from '../components/Slide'

interface SlideProps {
  isActive: boolean
}

const welcomeMessages = [
  { text: 'Welcome', lang: 'English' },
  { text: 'Dobrodošli', lang: 'Serbian' },
  { text: '欢迎', lang: 'Chinese' },
  { text: 'ようこそ', lang: 'Japanese' },
  { text: 'Bienvenue', lang: 'French' },
  { text: '환영합니다', lang: 'Korean' },
  { text: 'Willkommen', lang: 'German' },
  { text: 'स्वागत है', lang: 'Hindi' },
  { text: 'Bienvenido', lang: 'Spanish' },
  { text: 'Добро пожаловать', lang: 'Russian' },
  { text: 'Benvenuto', lang: 'Italian' },
  { text: 'خوش آمدید', lang: 'Urdu' },
  { text: 'Bem-vindo', lang: 'Portuguese' },
  { text: 'Hoş geldiniz', lang: 'Turkish' },
]

const announcementText = 'The presentation will begin shortly!'

export default function WelcomeSlide({ isActive }: SlideProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [typedText, setTypedText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (!isActive) return

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % welcomeMessages.length)
    }, 3500)

    return () => clearInterval(interval)
  }, [isActive])

  // Typing and erasing effect
  useEffect(() => {
    if (!isActive) return

    const typeSpeed = 50
    const deleteSpeed = 30
    const pauseBeforeDelete = 2000
    const pauseBeforeType = 500

    let timeout: ReturnType<typeof setTimeout>

    if (!isDeleting && typedText.length < announcementText.length) {
      // Typing
      timeout = setTimeout(() => {
        setTypedText(announcementText.slice(0, typedText.length + 1))
      }, typeSpeed)
    } else if (!isDeleting && typedText.length === announcementText.length) {
      // Pause before deleting
      timeout = setTimeout(() => {
        setIsDeleting(true)
      }, pauseBeforeDelete)
    } else if (isDeleting && typedText.length > 0) {
      // Deleting
      timeout = setTimeout(() => {
        setTypedText(announcementText.slice(0, typedText.length - 1))
      }, deleteSpeed)
    } else if (isDeleting && typedText.length === 0) {
      // Pause before typing again
      timeout = setTimeout(() => {
        setIsDeleting(false)
      }, pauseBeforeType)
    }

    return () => clearTimeout(timeout)
  }, [isActive, typedText, isDeleting])

  return (
    <Slide className="items-center justify-center text-center">
      {/* Animated background dots */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-electric-cyan/20"
            initial={{
              x: Math.random() * 100 + '%',
              y: Math.random() * 100 + '%',
            }}
            animate={{
              y: [null, '-100%'],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: 'linear',
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6">
        {/* Welcome text cycling */}
        <div className="h-32 md:h-40 flex items-center justify-center mb-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -30, scale: 0.9 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold text-gradient mb-2">
                {welcomeMessages[currentIndex].text}
              </h1>
              <span className="text-sm text-white/40">
                {welcomeMessages[currentIndex].lang}
              </span>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Starting soon message with typing effect */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="mb-8"
        >
          <p className="text-xl md:text-2xl text-white/60 font-light h-8">
            {typedText}
            <motion.span
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatType: 'reverse' }}
              className="inline-block w-[2px] h-[1.2em] ml-1 bg-electric-cyan align-middle"
            />
          </p>
        </motion.div>

        {/* Presenter info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.5 }}
          className="text-center"
        >
          <p className="text-lg text-white/80 font-medium">Alex Usachev</p>
        </motion.div>

      </div>

      {/* Navigation hint - positioned at bottom */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.5 }}
        className="absolute bottom-[100px] left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center text-white/40"
        >
          <span className="text-sm mb-2">Press arrow keys to navigate</span>
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </motion.div>
      </motion.div>
    </Slide>
  )
}
